/*
  Multiverse cart runtime — the ONE cart controller. Loaded by sections/cart-drawer.liquid; any
  section or block that needs the cart on a page without the drawer (cart_type page /
  notification) includes the same file, which sets itself up once.

  Multiverse.cart              add / change / update / get / refresh / render / open / close, the
                               state (`cart`, the last cart JSON) and the section registry.
  <cart-drawer>                the drawer element: open / close / focus trap, Dawn's
                               renderContents(parsedState) + getSectionsToRender() +
                               setActiveElement() contract (product-form.js, quick-add,
                               standard-actions keep working).
  <mv-cart-items>              the line-item list: quantity steppers, remove buttons, per-line
                               errors and the a11y live region.
  <mv-cart-quantity>           one stepper (minus / input / plus) → Multiverse.cart.change, after a
                               300 ms quiet period (Enter / leaving the field send at once).

  Every cart request carries `sections` for the Section Rendering API, so the drawer panel
  (#CartDrawer), the header bubble (#cart-icon-bubble), the live region and any
  [data-mv-cart-section] mount on the page (cart page sections) are repainted from one round trip.
  Requests are queued (one in flight at a time) so quick clicks cannot race each other; changes to
  the same line that are still waiting in the queue fold into one request (the last quantity wins).

  Events (bubbling on document):
    mv:cart-change { cart, sections, source, action }   after every successful repaint
    mv:cart-error  { message, source, response }         a request Shopify rejected or that failed
    mv:cart-open / mv:cart-close                          on <cart-drawer>
  Dawn's pubsub `cartUpdate` is published too ({ source: 'mv-cart', origin: <caller's source>,
  action, cartData }) and foreign `cartUpdate`s (quick-add-bulk, Standard Actions…) trigger a refresh.

  Contract: docs/superpowers/plans/pC-contracts.md
*/
(() => {
  window.Multiverse = window.Multiverse || {};
  const MV = window.Multiverse;
  if (MV.cartRuntimeLoaded) return;
  MV.cartRuntimeLoaded = true;

  const define = (name, cls) => {
    if (MV.defineOnce) MV.defineOnce(name, cls);
    else if (!customElements.get(name)) customElements.define(name, cls);
  };
  const routes = () =>
    Object.assign(
      { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update', cart_url: '/cart' },
      window.routes || {}
    );
  const strings = () =>
    Object.assign(
      { error: 'There was an error while updating your cart. Please try again.', quantityError: 'You can only add [quantity] of this item to your cart.' },
      window.cartStrings || {}
    );
  const OWN_SOURCES = new Set(['mv-cart']);
  // Quiet period after the last stepper click before its /cart/change.js goes out (Dawn's
  // ON_CHANGE_DEBOUNCE_TIMER): a run of clicks becomes one request and one repaint.
  const STEPPER_DEBOUNCE_MS = 300;
  // Callers that publish Dawn's cartUpdate AND call renderContents themselves.
  const SELF_RENDERING_SOURCES = new Set(['product-form', 'mv-cart']);
  const jsonHeaders = () => ({ 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' });
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const parseJSON = (text) => {
    try {
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  };

  // ---- state -------------------------------------------------------------------------------
  const cart = (MV.cart = MV.cart || {});
  cart.state = { cart: null, pending: 0 };
  let queue = Promise.resolve();
  const listeners = () => document.querySelector('cart-drawer');

  // Line keys with a request in flight (steppers stay clickable while one runs; the queue serialises
  // the requests and the fresh line keeps its spinner until its last request settles).
  const pendingLines = new Map();
  const trackLine = (key, delta) => {
    const next = Math.max(0, (pendingLines.get(key) || 0) + delta);
    if (next) pendingLines.set(key, next);
    else pendingLines.delete(key);
    return next;
  };
  const lineByKey = (key) => document.querySelector(`[data-mv-cart-line][data-key="${CSS.escape(key)}"]`);

  const readEmbeddedCart = (root = document) => {
    const script = root.querySelector('script[data-mv-cart-json]');
    return script ? parseJSON(script.textContent) : null;
  };

  const setCart = (data) => {
    if (data && typeof data.item_count === 'number' && Array.isArray(data.items)) cart.state.cart = data;
    return cart.state.cart;
  };

  const dispatch = (name, detail, target = document) => {
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  };
  // Dawn declares PUB_SUB_EVENTS as a top-level `const` (assets/constants.js): a global lexical
  // binding, NOT a window property, so it has to be reached by name, guarded with typeof.
  const dawnEvents = () => {
    try {
      return typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS || null;
    } catch (e) {
      return null;
    }
  };
  const publishDawn = (event, payload) => {
    const events = dawnEvents();
    if (typeof publish === 'function' && events && events[event]) return publish(events[event], payload);
    return Promise.resolve();
  };

  // Section Rendering API: render the sections for the page the customer is on, including its
  // query string (an alternate template `?view=` or a variant id changes what the page renders).
  const sectionsUrl = () => window.location.pathname + window.location.search;

  // ---- section registry ------------------------------------------------------------------
  // Each entry: { id: section id for the API, mount: live element, pick: selector inside the
  // fetched section HTML (falls back to the section wrapper) }.
  cart.sections = () => {
    const list = [];
    const seen = new Set();
    const push = (id, mount, pick) => {
      if (!id || !mount || seen.has(id)) return;
      seen.add(id);
      list.push({ id, mount, pick });
    };
    document.querySelectorAll('[data-mv-cart-section]').forEach((el) => {
      const id = el.dataset.mvCartSection;
      push(id, el, `[data-mv-cart-section="${id}"]`);
    });
    push('cart-icon-bubble', document.getElementById('cart-icon-bubble'), '.shopify-section');
    push('cart-live-region-text', document.getElementById('cart-live-region-text'), '.shopify-section');
    // Dawn's notification (cart_type = notification) renders its own sections.
    const notification = document.querySelector('cart-notification');
    if (notification && typeof notification.getSectionsToRender === 'function') {
      notification.getSectionsToRender().forEach((section) => {
        const mount = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        push(section.id, mount, section.selector || '.shopify-section');
      });
    }
    return list;
  };
  cart.sectionIds = () => cart.sections().map((s) => s.id);

  // Paint fetched section HTML into the mounts. Returns the ids that were painted.
  cart.render = (sections) => {
    if (!sections) return [];
    const painted = [];
    const drawer = listeners();
    const body = drawer ? drawer.querySelector('[data-mv-cart-body]') : null;
    const scrollTop = body ? body.scrollTop : 0;
    const focused = document.activeElement && document.activeElement.dataset ? document.activeElement.dataset.mvFocus : null;
    cart.sections().forEach(({ id, mount, pick }) => {
      const html = sections[id];
      if (typeof html !== 'string') return;
      const doc = parseHTML(html);
      const source = (pick && doc.querySelector(pick)) || doc.querySelector('.shopify-section') || doc.body;
      if (!source) return;
      mount.innerHTML = source.innerHTML;
      if (pick && pick.startsWith('[data-mv-cart-section')) {
        // The mount is the block's own element: carry its fresh attributes over as well.
        Array.from(source.attributes).forEach((attr) => mount.setAttribute(attr.name, attr.value));
      }
      if (id === 'cart-drawer' && drawer) {
        const fresh = doc.querySelector('cart-drawer');
        if (fresh) {
          drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
          drawer.dataset.cartCount = fresh.dataset.cartCount || '0';
        }
        const embedded = readEmbeddedCart(mount);
        if (embedded) setCart(embedded);
        pendingLines.forEach((count, key) => {
          const line = count ? lineByKey(key) : null;
          if (line) line.classList.add('mv-cart-item--loading');
        });
        const status = pendingLines.size ? mount.querySelector('[data-mv-cart-status]') : null;
        if (status) status.setAttribute('aria-hidden', 'false');
      }
      painted.push(id);
    });
    if (drawer) {
      const newBody = drawer.querySelector('[data-mv-cart-body]');
      if (newBody && scrollTop) newBody.scrollTop = scrollTop;
      if (drawer.classList.contains('is-open') && (focused || drawer.trapped) && typeof drawer.refocus === 'function') {
        // The panel's nodes are new: put the keyboard back on the same control (or, when it is gone
        // because its line was removed, on the close button) and re-arm the focus trap, which holds
        // references to the old nodes.
        drawer.refocus(focused ? drawer.querySelector(`[data-mv-focus="${CSS.escape(focused)}"]`) : null);
      }
      const live = drawer.querySelector('[data-mv-cart-live]');
      const panel = drawer.querySelector('[data-mv-cart-section="cart-drawer"]');
      if (live && panel && panel.dataset.mvCartLiveText) live.textContent = panel.dataset.mvCartLiveText;
    }
    return painted;
  };

  // ---- requests --------------------------------------------------------------------------
  const setBusy = (busy) => {
    cart.state.pending = Math.max(0, cart.state.pending + (busy ? 1 : -1));
    const drawer = listeners();
    if (drawer) {
      drawer.classList.toggle('mv-cart--loading', cart.state.pending > 0);
      drawer.setAttribute('aria-busy', cart.state.pending > 0 ? 'true' : 'false');
    }
    document.querySelectorAll('mv-cart-items').forEach((el) => el.setAttribute('aria-busy', cart.state.pending > 0 ? 'true' : 'false'));
  };

  const fail = (message, source, response) => {
    const error = new Error(message);
    error.response = response || null;
    error.source = source;
    dispatch('mv:cart-error', { message, source, response: response || null });
    return error;
  };

  const request = (url, body) =>
    fetch(url, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) }).then((response) =>
      response.text().then((text) => {
        const data = parseJSON(text) || {};
        if (!response.ok || data.status) {
          const message = data.description || data.message || strings().error;
          const error = new Error(message);
          error.response = data;
          error.httpStatus = response.status;
          throw error;
        }
        return data;
      })
    );

  // Serialise every mutation: a second click waits for the first round trip.
  const enqueue = (job) => {
    const run = queue.then(job, job);
    queue = run.catch(() => {});
    return run;
  };

  const complete = async ({ data, source, action, render, cartData, open }) => {
    let painted = [];
    if (render !== false && data && data.sections) painted = cart.render(data.sections);
    let current = cartData && typeof cartData.item_count === 'number' ? setCart(cartData) : cart.state.cart;
    if (!current || !painted.includes('cart-drawer')) current = await cart.get();
    dispatch('mv:cart-change', { cart: current, sections: data ? data.sections || null : null, source, action });
    // `source` stays 'mv-cart' (the foreign-refresh subscriber below filters on it); the caller's
    // own source travels as `origin` so blocks can ignore the cartUpdate they caused themselves.
    publishDawn('cartUpdate', { source: 'mv-cart', origin: source || 'mv-cart', action, cartData: current });
    const drawer = listeners();
    if (open && drawer && typeof drawer.open === 'function') drawer.open();
    return current;
  };

  // GET /cart.js → the cart JSON (also refreshes the state).
  cart.get = () =>
    fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((data) => setCart(data) || data);

  cart.getState = () => cart.state.cart;

  // items: [{ id, quantity, selling_plan?, properties? }] or a FormData / <form> of Dawn's add form.
  // options: { render (default true), open (default true when a drawer exists), source, activeElement, skipCart }
  cart.add = (items, options = {}) =>
    enqueue(async () => {
      const lines = normaliseLines(items);
      if (!lines.length) throw fail(strings().error, options.source || 'mv-cart');
      const body = { items: lines };
      const shouldRender = options.render !== false && !options.skipCart;
      if (shouldRender) {
        body.sections = cart.sectionIds();
        body.sections_url = sectionsUrl();
      }
      const drawer = listeners();
      if (drawer && options.activeElement && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(options.activeElement);
      setBusy(true);
      try {
        const data = await request(`${routes().cart_add_url}.js`, body);
        const first = Array.isArray(data.items) ? data.items[0] : data;
        if (!data.key && first && first.key) data.key = first.key;
        if (options.skipCart) return data;
        const notification = !drawer && document.querySelector('cart-notification');
        if (shouldRender && notification && typeof notification.renderContents === 'function') {
          try {
            notification.renderContents(data);
          } catch (e) {
            /* the notification markup is Dawn's; a repaint failure is not fatal */
          }
          await cart.get();
          dispatch('mv:cart-change', { cart: cart.state.cart, sections: data.sections || null, source: options.source || 'mv-cart', action: 'add' });
          publishDawn('cartUpdate', { source: 'mv-cart', origin: options.source || 'mv-cart', action: 'add', cartData: cart.state.cart, productVariantId: first && first.variant_id });
          return data;
        }
        if (shouldRender && !drawer && !notification) {
          window.location.href = routes().cart_url;
          return data;
        }
        await complete({ data, source: options.source || 'mv-cart', action: 'add', render: shouldRender, open: options.open !== false && shouldRender });
        return data;
      } catch (error) {
        throw fail(error.message, options.source || 'mv-cart', error.response);
      } finally {
        setBusy(false);
      }
    });

  // Changes to the same line that are queued but not yet started are folded into one request: the
  // later call overwrites the waiting body (its quantity wins) and shares that request's promise,
  // so a stepper that keeps being clicked while a round trip is in flight costs one more round
  // trip, not one per click. Only same-shaped calls fold (same source / render / open).
  const waitingChanges = new Map();
  const changeKey = (body) => (body.id !== undefined && body.id !== null ? `id:${body.id}` : body.line !== undefined ? `line:${body.line}` : null);
  const sameShape = (a, b) => a.source === b.source && a.render === b.render && a.open === b.open;

  // change(key | { id | line, quantity, properties?, selling_plan? }, quantity?, options?)
  cart.change = (target, quantity, options = {}) => {
    const body = typeof target === 'object' && target !== null ? { ...target } : { id: target, quantity };
    if (typeof target !== 'object' && quantity === undefined) throw new Error('Multiverse.cart.change needs a quantity');
    const key = changeKey(body);
    const waiting = key ? waitingChanges.get(key) : null;
    if (waiting && !waiting.started && sameShape(waiting.options, options)) {
      Object.assign(waiting.body, body);
      return waiting.promise;
    }
    const entry = { body, options, started: false, promise: null };
    entry.promise = enqueue(async () => {
      entry.started = true;
      if (key && waitingChanges.get(key) === entry) waitingChanges.delete(key);
      const payload = entry.body;
      if (options.render !== false) {
        payload.sections = cart.sectionIds();
        payload.sections_url = sectionsUrl();
      }
      setBusy(true);
      try {
        const data = await request(`${routes().cart_change_url}.js`, payload);
        await complete({ data, cartData: data, source: options.source || 'mv-cart', action: payload.quantity === 0 ? 'remove' : 'change', render: options.render, open: options.open });
        return data;
      } catch (error) {
        throw fail(error.message, options.source || 'mv-cart', error.response);
      } finally {
        setBusy(false);
      }
    });
    if (key) waitingChanges.set(key, entry);
    return entry.promise;
  };

  // update({ updates?, note?, attributes? }, options?) → POST /cart/update.js
  cart.update = (payload = {}, options = {}) =>
    enqueue(async () => {
      const body = { ...payload };
      if (options.render !== false) {
        body.sections = cart.sectionIds();
        body.sections_url = sectionsUrl();
      }
      setBusy(true);
      try {
        const data = await request(`${routes().cart_update_url}.js`, body);
        await complete({ data, cartData: data, source: options.source || 'mv-cart', action: 'update', render: options.render, open: options.open });
        return data;
      } catch (error) {
        throw fail(error.message, options.source || 'mv-cart', error.response);
      } finally {
        setBusy(false);
      }
    });

  // Re-fetch the sections (and the cart) without changing anything. `first` may be a line key the
  // notification should show, or an options object.
  cart.refresh = (first, options = {}) => {
    const opts = typeof first === 'object' && first !== null ? first : { key: first, ...options };
    return enqueue(async () => {
      const ids = cart.sectionIds();
      setBusy(true);
      try {
        // Section Rendering API. No `Accept: application/json` here: with that header Shopify
        // answers /cart with the cart JSON (as /cart.js) instead of the rendered sections.
        const sections = ids.length ? await fetch(`${routes().cart_url}?sections=${ids.join(',')}`).then((r) => r.json()) : {};
        // Dawn's notification pops open on renderContents, so only an explicit `open` goes that way;
        // a plain refresh repaints its registered sections (bubble, live region) silently.
        const notification = opts.open && !listeners() && document.querySelector('cart-notification');
        if (notification && typeof notification.renderContents === 'function') {
          const current = await cart.get();
          const key = opts.key || (current.items && current.items[0] ? current.items[0].key : null);
          try {
            notification.renderContents({ key, sections, items: [] });
          } catch (e) {
            /* nothing to repaint */
          }
          dispatch('mv:cart-change', { cart: current, sections, source: opts.source || 'mv-cart', action: 'refresh' });
          return current;
        }
        return complete({ data: { sections }, cartData: opts.cart, source: opts.source || 'mv-cart', action: 'refresh', open: opts.open });
      } finally {
        setBusy(false);
      }
    });
  };

  cart.open = () => {
    const drawer = listeners();
    if (drawer && typeof drawer.open === 'function') drawer.open();
  };
  cart.close = () => {
    const drawer = listeners();
    if (drawer && typeof drawer.close === 'function') drawer.close();
  };

  // ---- helpers ---------------------------------------------------------------------------
  function normaliseLines(items) {
    if (items instanceof HTMLFormElement) items = new FormData(items);
    if (items instanceof FormData) {
      const line = { id: Number(items.get('id')), quantity: Math.max(1, parseInt(items.get('quantity'), 10) || 1) };
      const plan = items.get('selling_plan');
      if (plan) line.selling_plan = Number(plan);
      const properties = {};
      for (const [name, value] of items.entries()) {
        const match = /^properties\[(.+)\]$/.exec(name);
        if (match && value !== '') properties[match[1]] = value;
      }
      if (Object.keys(properties).length) line.properties = properties;
      return line.id ? [line] : [];
    }
    const list = Array.isArray(items) ? items : items ? [items] : [];
    return list
      .filter((item) => item && item.id)
      .map((item) => {
        const line = { id: Number(item.id), quantity: Math.max(1, parseInt(item.quantity, 10) || 1) };
        if (item.selling_plan) line.selling_plan = Number(item.selling_plan);
        if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
        return line;
      });
  }

  // Foreign cart changes (Dawn quick-add-bulk, Standard Actions, apps publishing Dawn's event)
  // → repaint from the server. Callers that repaint through renderContents
  // themselves are skipped.
  const foreignEvents = dawnEvents();
  if (typeof subscribe === 'function' && foreignEvents && foreignEvents.cartUpdate) {
    let timer = 0;
    subscribe(foreignEvents.cartUpdate, (event) => {
      const source = event && event.source;
      if (source && (SELF_RENDERING_SOURCES.has(source) || OWN_SOURCES.has(source))) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => cart.refresh({ cart: event.cartData, source }).catch(() => {}), 50);
    });
  }

  // ---- <cart-drawer> ---------------------------------------------------------------------
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.trapped = false;
      this.activeElement = null;
      this.onClick = (event) => {
        if (event.target.closest('[data-mv-cart-close]')) {
          event.preventDefault();
          this.close();
        }
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      };
      this.onDocumentClick = (event) => {
        if (!(event.target instanceof Element)) return;
        const trigger = event.target.closest('#cart-icon-bubble, [data-mv-cart-open]');
        if (!trigger || this.contains(trigger)) return;
        event.preventDefault();
        this.open(trigger);
      };
      this.onDocumentKeydown = (event) => {
        if (event.code !== 'Space' || !(event.target instanceof Element)) return;
        const trigger = event.target.closest('#cart-icon-bubble');
        if (!trigger) return;
        event.preventDefault();
        this.open(trigger);
      };
      this.onSectionSelect = (event) => {
        if (event.target === this.closest('.shopify-section') || this.contains(event.target)) this.open(null, { lock: false, focus: false });
      };
      this.onSectionDeselect = (event) => {
        if (this.testMode) return;
        if (event.target === this.closest('.shopify-section') || this.contains(event.target)) this.close();
      };
      // Drawer-level errors: a line's own failure is shown under that line by <mv-cart-items>;
      // everything else (a failed add / refresh from a block or the API) lands in the panel's
      // role=alert slot and is cleared by the next successful cart change.
      this.onCartError = (event) => {
        const detail = event.detail || {};
        if (detail.source === 'mv-cart-items') return;
        this.setError(detail.message || strings().error);
      };
      this.onCartChange = () => this.setError('');
    }

    connectedCallback() {
      this.testMode = this.dataset.testMode === 'true';
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('mv:cart-error', this.onCartError);
      document.addEventListener('mv:cart-change', this.onCartChange);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:block:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      const link = document.getElementById('cart-icon-bubble');
      if (link) {
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');
      }
      const embedded = readEmbeddedCart(this);
      if (embedded) setCart(embedded);
      if (this.testMode) this.open(null, { lock: false, focus: false });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('mv:cart-error', this.onCartError);
      document.removeEventListener('mv:cart-change', this.onCartChange);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:block:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.isOpen) this.close();
    }

    get panel() {
      return this.querySelector('#CartDrawer');
    }

    open(triggeredBy, { lock = true, focus = true } = {}) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      if (lock) document.body.classList.add('mv-drawer-open');
      this.trapped = Boolean(focus && typeof trapFocus === 'function');
      if (this.trapped) this.refocus(null);
      dispatch('mv:cart-open', { cart: cart.state.cart }, this);
      const items = this.querySelector('mv-cart-items');
      if (items && typeof items.dispatchViewEvent === 'function') items.dispatchViewEvent();
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      document.body.classList.remove('mv-drawer-open');
      if (this.trapped && typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      this.trapped = false;
      dispatch('mv:cart-close', { cart: cart.state.cart }, this);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    setError(message) {
      const slot = this.querySelector('[data-mv-cart-errors]');
      if (slot) slot.textContent = message || '';
    }

    // The focus container for the current state (the empty box or the whole panel) and the
    // control the keyboard lands on by default: that state's close BUTTON (never the overlay div).
    focusTargets() {
      const panel = this.panel;
      if (!panel) return null;
      const container = (this.classList.contains('is-empty') && panel.querySelector('.mv-cart__empty')) || panel;
      const focusable = typeof getFocusableElements === 'function' ? getFocusableElements(container) : [];
      const fallback = container.querySelector('button[data-mv-cart-close]') || focusable[0] || container;
      return { container, fallback };
    }

    // Move the keyboard to `preferred` (a control inside the current container) or to the default
    // control, and, while the drawer traps focus, (re)arm the trap on the CURRENT nodes. Called
    // when the drawer opens and after every repaint of #CartDrawer, because Dawn's trapFocus keeps
    // references to the first/last focusable elements it saw and those are replaced on repaint.
    refocus(preferred) {
      const targets = this.focusTargets();
      if (!targets) return;
      const element = preferred && targets.container.contains(preferred) ? preferred : targets.fallback;
      element.focus({ preventScroll: true });
      if (this.trapped && typeof trapFocus === 'function') trapFocus(targets.container, element);
    }

    // Dawn contract: product-form.js / quick-add pass the /cart/add.js response with `sections`.
    // Queued like every mutation so the paint never lands in the middle of a Multiverse.cart round
    // trip (a queued change would otherwise repaint over it a moment later).
    renderContents(parsedState) {
      return enqueue(async () => {
        const sections = parsedState && parsedState.sections;
        const painted = cart.render(sections);
        if (!painted.includes('cart-drawer')) this.classList.remove('is-empty');
        const looksLikeCart = parsedState && typeof parsedState.item_count === 'number' && Array.isArray(parsedState.items);
        const current = looksLikeCart ? setCart(parsedState) : painted.includes('cart-drawer') && cart.state.cart ? cart.state.cart : await cart.get();
        if (current) this.classList.toggle('is-empty', current.item_count === 0);
        dispatch('mv:cart-change', { cart: current, sections: sections || null, source: 'render', action: 'add' });
        window.setTimeout(() => this.open());
        return current;
      });
    }

    getSectionsToRender() {
      return cart.sections().map(({ id, pick }) => ({ id, selector: pick === '.shopify-section' ? undefined : pick }));
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return parseHTML(html).querySelector(selector).innerHTML;
    }
  }
  define('cart-drawer', CartDrawer);

  // ---- <mv-cart-items> -------------------------------------------------------------------
  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.onClick = (event) => {
        const remove = event.target.closest('[data-mv-cart-remove]');
        if (!remove) return;
        event.preventDefault();
        const line = remove.closest('[data-mv-cart-line]');
        if (!line) return;
        // A stepper value still waiting for this line is moot once the line goes.
        const stepper = line.querySelector('mv-cart-quantity');
        if (stepper && typeof stepper.cancel === 'function') stepper.cancel();
        this.changeLine(line, 0, remove);
      };
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.status = this.querySelector('[data-mv-cart-status]');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    // Standard Events (cart:view) when the drawer opens, if the bundle is on the page.
    dispatchViewEvent() {
      const { CartViewEvent } = window.StandardEvents || {};
      if (!CartViewEvent || !cart.state.cart || !cart.state.cart.currency) return;
      try {
        this.dispatchEvent(new CartViewEvent({ context: 'dialog', cart: CartViewEvent.createCartFromAjaxResponse(cart.state.cart) }));
      } catch (e) {
        /* optional analytics */
      }
    }

    setLineError(line, message) {
      const slot = line.querySelector('[data-mv-cart-line-error] .mv-cart-item__error-text');
      if (slot) slot.textContent = message || '';
      line.classList.toggle('mv-cart-item--error', Boolean(message));
    }

    changeLine(line, quantity, trigger) {
      const key = line.dataset.key;
      const previous = parseInt(line.dataset.quantity, 10) || 0;
      if (!key || quantity === previous) return Promise.resolve();
      line.classList.add('mv-cart-item--loading');
      trackLine(key, 1);
      if (this.status) this.status.setAttribute('aria-hidden', 'false');
      if (trigger && trigger.dataset && trigger.dataset.mvFocus) trigger.focus({ preventScroll: true });
      const lineKey = line.dataset.key;
      const variantId = line.dataset.variantId;
      // The panel is repainted by the response, so anything after the round trip addresses the
      // line by key, never by the node this click landed on.
      const status = () => document.querySelector('mv-cart-items [data-mv-cart-status]') || this.status;
      const items = () => document.querySelector('mv-cart-items') || this;
      return cart
        .change({ id: key, quantity }, undefined, { source: 'mv-cart-items' })
        .then((data) => {
          // Shopify capped the quantity (stock limit): the repainted line shows the real value.
          const item = Array.isArray(data.items) ? data.items.find((i) => i.key === lineKey) : null;
          if (item && quantity > 0 && item.quantity !== quantity) {
            const fresh = lineByKey(lineKey);
            if (fresh) items().setLineError(fresh, strings().quantityError.replace('[quantity]', item.quantity));
          }
          this.dispatchLinesEvent(quantity === 0 ? 'remove' : 'update', lineKey, variantId, quantity, data);
        })
        .catch((error) => {
          const fresh = lineByKey(lineKey) || line;
          const input = fresh.querySelector('.mv-cart-qty__input');
          if (input) input.value = input.dataset.value || previous;
          items().setLineError(fresh, (error && error.message) || strings().error);
        })
        .finally(() => {
          const left = trackLine(key, -1);
          line.classList.remove('mv-cart-item--loading');
          const fresh = lineByKey(lineKey);
          if (fresh && !left) fresh.classList.remove('mv-cart-item--loading');
          const s = status();
          if (s && !pendingLines.size) s.setAttribute('aria-hidden', 'true');
        });
    }

    dispatchLinesEvent(action, lineKey, variantId, quantity, data) {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (!CartLinesUpdateEvent || !lineKey) return;
      try {
        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines: [{ id: lineKey, quantity }], promise: deferred.promise }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data) });
      } catch (e) {
        /* optional analytics */
      }
    }
  }
  define('mv-cart-items', CartItems);

  // ---- <mv-cart-quantity> ----------------------------------------------------------------
  class CartQuantity extends HTMLElement {
    constructor() {
      super();
      this.committed = null;
      this.waiting = null;
      this.timer = 0;
      this.onClick = (event) => {
        const button = event.target.closest('button[name]');
        if (!button) return;
        event.preventDefault();
        const step = this.step;
        // Like Dawn's <quantity-input> (stepUp / stepDown): minus stops at the quantity rule's
        // minimum; removing the line is the remove button's job.
        const value = button.name === 'plus' ? this.value + step : Math.max(this.min, this.value - step);
        if (value === this.value) return;
        this.commit(value, button);
      };
      this.onChange = (event) => {
        if (event.target !== this.input) return;
        this.commit(parseInt(this.input.value, 10), this.input);
      };
      this.onKeydown = (event) => {
        if (event.key === 'Enter' && event.target === this.input) {
          event.preventDefault();
          this.commit(parseInt(this.input.value, 10), this.input, true);
        }
      };
      // A typed value goes out as soon as the keyboard leaves the field (its `change` fired first).
      this.onFocusout = (event) => {
        if (event.target === this.input) this.flush();
      };
    }

    connectedCallback() {
      this.input = this.querySelector('input');
      this.committed = null;
      this.waiting = null;
      this.timer = 0;
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusout', this.onFocusout);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusout', this.onFocusout);
      // Repainted away (another request landed) with clicks still waiting: send them now, against
      // the fresh line, rather than lose them.
      this.flush();
    }

    get value() {
      return parseInt(this.input ? this.input.value : '0', 10) || 0;
    }
    get step() {
      return Math.max(1, parseInt(this.input ? this.input.step : '1', 10) || 1);
    }
    get min() {
      return Math.max(0, parseInt(this.input ? this.input.min : '0', 10) || 0);
    }

    validate(value) {
      const min = this.min;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      const step = this.step;
      const q = window.quickOrderListStrings || {};
      if (Number.isNaN(value) || value < 0 || (value > 0 && value < min)) return (q.min_error || 'Minimum of [min]').replace('[min]', Math.max(min, 1));
      if (max !== null && value > max) return (q.max_error || 'Maximum of [max]').replace('[max]', max);
      if (value > 0 && value % step !== 0) return (q.step_error || 'Increments of [step]').replace('[step]', step);
      return '';
    }

    // Validate, show the value at once, and send it after a short quiet period (like Dawn's
    // ON_CHANGE_DEBOUNCE_TIMER): a run of clicks becomes one request. `immediate` (Enter) skips the
    // wait.
    commit(value, trigger, immediate = false) {
      const line = this.closest('[data-mv-cart-line]');
      const items = this.closest('mv-cart-items');
      if (!line || !items) return;
      const message = this.validate(value);
      if (message) {
        this.cancel();
        this.input.value = this.input.dataset.value || line.dataset.quantity;
        items.setLineError(line, message);
        return;
      }
      items.setLineError(line, '');
      this.input.value = value;
      // Enter commits here AND makes the browser fire its own `change` for the same value: one
      // request. `committed` is cleared once the request settles (a failed retry must go through).
      if (value === this.committed) {
        this.cancel();
        return;
      }
      const focusKey = trigger && trigger.dataset ? trigger.dataset.mvFocus : null;
      this.waiting = { value, focusKey, key: line.dataset.key };
      window.clearTimeout(this.timer);
      if (immediate) this.flush();
      else this.timer = window.setTimeout(() => this.flush(), STEPPER_DEBOUNCE_MS);
    }

    cancel() {
      window.clearTimeout(this.timer);
      this.timer = 0;
      this.waiting = null;
    }

    // Send the waiting value. The panel may have been repainted since the click, so the line, the
    // list and the control to refocus are looked up again by key.
    flush() {
      const waiting = this.waiting;
      this.cancel();
      if (!waiting) return;
      const { value, key, focusKey } = waiting;
      const line = (key && lineByKey(key)) || this.closest('[data-mv-cart-line]');
      const items = line ? line.closest('mv-cart-items') : null;
      if (!line || !items) return;
      const trigger = (focusKey && line.querySelector(`[data-mv-focus="${CSS.escape(focusKey)}"]`)) || this.input;
      this.committed = value;
      const settled = () => {
        if (this.committed === value) this.committed = null;
      };
      items.changeLine(line, value, trigger).then(settled, settled);
    }
  }
  define('mv-cart-quantity', CartQuantity);
})();
